import { useLayoutEffect, useState } from 'react';

import { ThemeType } from '@infoods/lifebase-ui';

import { getService } from 'react-in-angularjs';

import { NavigationIconType } from '../constants/system/navigationIcons';
import { SolutionType } from '../constants/user-info/solutionType';

type Breadcrumb = {
  label: string;
  icon: NavigationIconType;
};

export type PageConfig = {
  /**
   * Hides both sidebar (NgzSidebar) and top bar (NgzTopBar).
   */
  hideNavigation?: boolean;
  /**
   * Optional theme override for this route. Currently only 'swymo' exists;
   * kept as an option for a future light/dark theme.
   */
  theme?: ThemeType;
  breadcrumbs?: Breadcrumb[];
  /**
   * Controls active solution when navigating to this route.
   * - SolutionType: set that solution
   * - null / undefined (not set): clear active solution (outside any solution)
   */
  solution?: SolutionType | null;
};

/**
 * Hook to access page configuration from AngularJS route configuration.
 * Listens to $routeChangeSuccess events to update when route changes.
 *
 * @example
 * // In route config:
 * $routeProvider.when('/some-route', {
 *   pageConfig: { hideNavigation: true, theme: 'dark' }
 * });
 */
export const usePageConfig = (): PageConfig => {
  const getPageConfig = (): PageConfig => {
    const $route = getService('$route');
    return $route?.current?.pageConfig || {};
  };

  const [pageConfig, setPageConfig] = useState<PageConfig>(getPageConfig);

  const updatePageConfig = () => {
    const newPageConfig = getPageConfig();

    setPageConfig(newPageConfig);
  };

  useLayoutEffect(() => {
    const $rootScope = getService('$rootScope');

    const unsubscribeRouteUpdate = $rootScope.$on(
      '$routeChangeSuccess',
      updatePageConfig,
    );

    return () => {
      unsubscribeRouteUpdate();
    };
  }, []);

  return pageConfig;
};
